// frontend/src/pages/ProfilePage.js
import { useState, useEffect } from 'react';
import { Card, Descriptions, Button, Spin, Typography, message, Modal, Form, Input, Space, Upload, Avatar, Tooltip } from 'antd'; // Added Tooltip
import { EditOutlined, KeyOutlined, UserOutlined, UploadOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'; // Added InfoCircleOutlined
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';

import { getProfile, updateProfile, changePassword, removeProfilePicture } from '../services/profileService';
import { useProfile } from '../context/ProfileContext';
import { imageBaseUrl } from '../services/api';

const { Title } = Typography;
const { confirm } = Modal;

// --- NEW: Password Validation Rules ---
const passwordRules = [
    { required: true, message: 'Please enter your password!' },
    { min: 8, message: 'Minimum 8 characters.' },
    { pattern: /[A-Z]/, message: 'Must contain one uppercase letter.' },
    { pattern: /[a-z]/, message: 'Must contain one lowercase letter.' },
    { pattern: /[0-9]/, message: 'Must contain one number.' },
    {
        validator: (_, value) => {
            if (!value || /[!@#$%^&*()]/.test(value)) {
                return Promise.resolve();
            }
            return Promise.reject('Must contain one special character (!@#$%^&*()).');
        }
    }
];


const ProfilePage = () => {
    const { token } = useAuth();
    const { refreshProfileData } = useProfile();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [uploading, setUploading] = useState(false);
    const [imageVersion, setImageVersion] = useState(Date.now());

    // Use imageBaseUrl from api.js

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const data = await getProfile();
            setProfile(data);
        } catch (error) {
            message.error('Failed to load profile data.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditProfile = () => {
        editForm.setFieldsValue({
            fullName: profile.full_name,
            email: profile.email,
            phoneNumber: profile.phone_number,
        });
        setIsEditModalVisible(true);
    };

    const handleUpdateProfile = async (values) => {
        try {
            await updateProfile(values);
            message.success('Profile updated successfully!');
            setIsEditModalVisible(false);
            fetchProfile(); // Update local state for this page
            if (refreshProfileData) refreshProfileData(); // Update layout header/profile
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to update profile.');
        }
    };

    const handleChangePassword = async (values) => {
        try {
            await changePassword(values);
            message.success('Password changed successfully!');
            setIsPasswordModalVisible(false);
            passwordForm.resetFields();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to change password.');
        }
    };

    const { modal: appModal } = useNotification();

    const handleRemovePicture = async () => {
        // console.log('Remove Profile Picture Triggered');
        const modalConfirm = appModal?.confirm || confirm; // prefer app modal
        modalConfirm({
            title: 'Are you sure you want to remove your profile picture?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone.',
            async onOk() {
                try {
                    const updatedProfile = await removeProfilePicture();
                    message.success('Profile picture removed!');
                    // Re-fetch the profile to ensure the page shows the latest data
                    await fetchProfile();
                    // bump imageVersion to force the avatar to reload in case of caching
                    setImageVersion(Date.now());
                    if (refreshProfileData) refreshProfileData();
                } catch (error) {
                    message.error(error.response?.data?.error || 'Failed to remove profile picture.');
                }
            },
        });
    };

    // Determine the action URL for the upload component
    const uploadActionURL = `${imageBaseUrl}/api/profile/upload-picture`;

    const uploadProps = {
        name: 'profilePicture',
        action: uploadActionURL,
        headers: {
            Authorization: `Bearer ${token}`,
        },
        accept: '.jpeg,.jpg,.png,.gif',
        showUploadList: false,
        beforeUpload: (file) => {
            const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
            if (!isJpgOrPng) {
                message.error('You can only upload JPG/PNG/GIF file!');
            }
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('Image must smaller than 5MB!');
            }
            return isJpgOrPng && isLt5M;
        },
        onChange(info) {
            if (info.file.status === 'uploading') {
                setUploading(true);
                return;
            }
            if (info.file.status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
                setUploading(false);
                setProfile(prev => ({ ...prev, profile_picture_url: info.file.response.profile_picture_url }));
                // bump image version and refresh layout/profile data
                setImageVersion(Date.now());
                if (refreshProfileData) refreshProfileData();
            } else if (info.file.status === 'error') {
                message.error(`${info.file.name} file upload failed: ${info.file.response?.error || 'Unknown error'}`);
                setUploading(false);
            }
        },
    };

    if (loading) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;
    }


    const profilePictureFullUrl = profile?.profile_picture_url ? `${imageBaseUrl}${profile.profile_picture_url}?cb=${imageVersion}` : null;


    return (
        <div style={{ margin: '0 auto' }}>
            <Title level={2}>My Profile</Title>
            <Card
                title={<Space><UserOutlined /> Personal Information</Space>}
                extra={
                    <Space>
                        <Button icon={<KeyOutlined />} onClick={() => setIsPasswordModalVisible(true)}>Change Password</Button>
                        <Button type="default" icon={<EditOutlined />} onClick={handleEditProfile}>Edit Profile</Button>
                    </Space>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                    <Avatar
                        size={128}
                        src={profilePictureFullUrl}
                        icon={<UserOutlined />}
                        style={{ marginBottom: 16 }}
                    />
                    <Space>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined />} loading={uploading}>Upload Photo</Button>
                        </Upload>
                        {profile?.profile_picture_url && (
                            <Button danger onClick={handleRemovePicture}>Remove Photo</Button>
                        )}
                    </Space>
                </div>

                <Descriptions bordered column={1}>
                    <Descriptions.Item label="Full Name">{profile?.full_name || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Username">{profile?.username}</Descriptions.Item>
                    <Descriptions.Item label="Role">{profile?.role.replace('_', ' ').toUpperCase()}</Descriptions.Item>
                    <Descriptions.Item label="Email">{profile?.email || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Phone Number">{profile?.phone_number || 'N/A'}</Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Edit Profile Modal (unchanged) */}
            <Modal okType='default' title="Edit Profile" open={isEditModalVisible} onCancel={() => setIsEditModalVisible(false)} onOk={() => editForm.submit()} okText="Save Changes">
                <Form form={editForm} layout="vertical" onFinish={handleUpdateProfile}>
                    <Form.Item name="fullName" label="Full Name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="phoneNumber" label="Phone Number">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Change Password Modal (Updated rules) */}
            <Modal
                okType='default'
                title="Change Password"
                open={isPasswordModalVisible}
                onCancel={() => setIsPasswordModalVisible(false)}
                onOk={() => passwordForm.submit()}
                okText="Change Password"
            >
                <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
                    <Form.Item name="currentPassword" label="Current Password" rules={[{ required: true }]}>
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label={
                            <Space>
                                New Password
                                <Tooltip title="Must be 8+ chars, include uppercase, lowercase, number, and one special character (!@#$%^&*()).">
                                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                                </Tooltip>
                            </Space>
                        }
                        rules={passwordRules}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="confirmNewPassword"
                        label="Confirm New Password"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Please confirm your new password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProfilePage;